import cls from 'classnames';
import { useCallback, useRef, useEffect, useMemo } from 'react';
import { Button, Overlay, Popover, CloseButton, Tabs, Tab, Form } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import { EventPriority } from 'src/components/Trap/Elements';
import { useEffectState } from 'src/utils/hooks';
import { isValidPosition } from 'src/utils/helpers';

import classnames from './SidePanel.module.scss';

export const SidePanel = ({ groups, traps, activeTab, onTabChange, selectedItem, editMode, onEditMode, onSelectItem, onDropItem, canEditItem, canToggleEditMode = false, availableGroupIds }) => {
  const listRef = useRef();
  const headerRef = useRef();
  const showPopover = useRef(true);
  const [popover, setPopover] = useEffectState(() => canToggleEditMode && editMode, [editMode, canToggleEditMode]);
  const history = useHistory();

  const instructions = {
    groups: __('In order to place a group onto a map, click on the green arrow icon on the right side of the group name in the list below. A needle indicating the group position will then be placed in the middle of the map, and you can now drag and drop the needle. Use the zoom buttons to define a more precise placement of the group.'),
    traps: __('In order to place a trap onto a map, click on Arrow Down icon next to the trap name in the list above. A needle indicating trap position will then be placed in the middle of the map, and you can now drag and drop the needle. Use the zoom buttons to define a more precise placement of a trap.'),
  };

  const items = useMemo(() => activeTab === 'traps' ? traps : groups, [activeTab, traps, groups]);

  useEffect(() => {
    if (listRef.current && selectedItem) {
      const index = selectedItem.type === 'trap' ? selectedItem.index : selectedItem.index;
      if (index !== undefined) {
        listRef.current.scrollToItem(index);
      }
    }
  }, [selectedItem, activeTab]);

  useEffect(() => {
    if (!canToggleEditMode) {
      showPopover.current = false;
      return;
    }
    showPopover.current = true;
  }, [canToggleEditMode]);

  function handleHidePopover() {
    showPopover.current = false;
    setPopover(false);
  }

  function handleTabSelect(tabKey) {
    const nextTab = tabKey || activeTab;
    onTabChange(nextTab);
    if (listRef.current) {
      listRef.current.scrollToItem(0);
    }
  }

  return (
    <div className="w-100 h-100 d-flex flex-column flex-nowrap">
      {canToggleEditMode && (
        <div ref={headerRef} className='d-flex flex-row gap-col-3 px-3 pt-2 pb-2 justify-content-end align-items-center'>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted mb-0 mt-2">{__('Edit mode')}</span>
            <Form.Check
              type="switch"
              id="map-edit-switch"
              className="fs-4 m-0 align-self-center"
              checked={editMode}
              onChange={() => onEditMode(!editMode)}
              aria-label={__('Toggle edit mode')}
            />
          </div>
        </div>
      )}
      <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="px-3 pt-2">
        <Tab eventKey="groups" title={__('Groups')} />
        <Tab eventKey="traps" title={__('Traps')} />
      </Tabs>

      <div className="w-100 h-100">
        <AutoSizer>
          {({ height, width }) => (
            <FixedSizeList ref={listRef} height={height} width={width} itemCount={items.length} itemSize={49}>
              {(({ index, style }) => {
                const item = items[index];
                const canEdit = typeof canEditItem === 'function' ? canEditItem(item) : true;
                const isSelected = selectedItem && (
                  (item.type === 'group' && Number(item.group_id) === Number(selectedItem.group_id)) ||
                  (item.type === 'trap' && Number(item.trap_id) === Number(selectedItem.trap_id))
                );

                return (
                  <ItemRow item={item} isSelected={isSelected} editMode={editMode}
                    canEdit={canEdit} onSelect={onSelectItem} onDrop={onDropItem} style={style}
                    onNavigate={history.push} availableGroupIds={availableGroupIds}
                  />
                );
              })}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>

      {canToggleEditMode && (
        <Overlay target={headerRef.current} show={popover && showPopover.current} placement='bottom' rootClose onHide={handleHidePopover} >
          {(props) => (
            <Popover id="popover-basic" {...props} className="edit-mode-popover">
              <Popover.Header><CloseButton onClick={handleHidePopover} /></Popover.Header>
              <Popover.Body>{instructions[activeTab]}</Popover.Body>
            </Popover>
          )}
        </Overlay>
      )}

    </div >
  )
}

const ItemRow = ({ item, isSelected, editMode, canEdit, onSelect, onDrop, style, onNavigate, availableGroupIds }) => {
  const title = item.group_name ?? item.trap_name;
  const { changed } = item;

  const handleItemClick = useCallback(() => onSelect(item), [item, onSelect]);
  const handleDropClick = useCallback(() => editMode && canEdit && onDrop(item), [item, editMode, canEdit, onDrop]);
  const handleNavigate = useCallback((event, href) => {
    event.preventDefault();
    event.stopPropagation();
    if (href && typeof onNavigate === 'function') {
      onNavigate(href);
    }
  }, [onNavigate]);

  const savedMapId = item.type === 'trap' && Number.isFinite(item?.buildingMapId) ? Number(item.buildingMapId) : null;
  const savedMapGroupId = item.type === 'trap' && Number.isFinite(item?.buildingMapGroupId) ? Number(item.buildingMapGroupId) : null;
  const groupIsAvailable = availableGroupIds instanceof Set ? availableGroupIds.has(savedMapGroupId) : true;
  const showBuildingLink = Number.isFinite(savedMapId) && Number.isFinite(savedMapGroupId) && groupIsAvailable;
  const linkHref = showBuildingLink
    ? `/groups/${savedMapGroupId}/maps/${savedMapId}/traps?trap_id=${item.trap_id}`
    : null;

  return (
    <div className={cls(classnames.item, { [classnames.active]: isSelected })} style={style} onClick={handleItemClick} >
      <span className={classnames.icon}>
        {item.type === 'trap' && (
          <EventPriority
            priority={item.event_priority}
            isCaught={item.is_caught}
            eventName={item.event_name}
          />
        )}
      </span>
      <span className={classnames.text}>
        {title}
        {item.type === 'trap' && item?.serial_no && (
          <div className={cls(classnames.serial, "text-muted")}>{item.serial_no}</div>
        )}
      </span>
      {item.type === 'trap' && (
        <div className="ms-2 me-2 d-inline-flex align-items-center justify-content-center">
          {linkHref ? (
            <a
              href={linkHref}
              className="d-inline-flex align-items-center justify-content-center text-muted"
              onClick={(e) => handleNavigate(e, linkHref)}
              title={__('Building Map')}
              aria-label={__('Building Map')}
              style={{ textDecoration: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#495057'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = ''; }}
            >
              <i className="bi bi-box-arrow-right" aria-hidden="true" />
            </a>
          ) : (
            <span className="d-inline-flex align-items-center justify-content-center invisible">
              <i className="bi bi-box-arrow-right" aria-hidden="true" />
            </span>
          )}
        </div>
      )}
      {canEdit && !isValidPosition(changed) &&
        <div className={cls(classnames.location, { [classnames.active]: editMode })} onClick={handleDropClick} />
      }
    </div>
  )
}
